import { Box, Pagination, Typography } from '@mui/material';

interface SectionPagerProps {
  // Accessible name of the page buttons' <nav>; both sections render a pager,
  // so each needs its own.
  label: string;
  // The page the user asked for; the buttons show it straight away.
  page: number;
  // The page last returned by merge/rows, and how many rows it held.
  loadedPage: number;
  rowsOnPage: number;
  totalRows: number;
  pageSize: number;
  disabled: boolean;
  onChange: (page: number) => void;
}

/**
 * "Rows 101-200 of 250" plus page buttons under a section's grid.
 *
 * merge/rows returns one page of a section, so without this the rows past the
 * first page could never be shown or decided, while their conflicts still
 * counted against Submit. The range describes the rows actually on screen, so
 * its text is blank while an empty page is showing (a page past the end that
 * is being replaced by the last one). The range is a polite live region, so a
 * page change is announced once its rows arrive. The buttons appear only when
 * there is more than one page, and stay mounted across that empty page so a
 * focused button is not removed from under the keyboard.
 */
const SectionPager = ({
  label,
  page,
  loadedPage,
  rowsOnPage,
  totalRows,
  pageSize,
  disabled,
  onChange,
}: SectionPagerProps) => {
  if (totalRows <= 0) {
    return null;
  }
  const pageCount = Math.max(1, Math.ceil(totalRows / pageSize));
  const first = (loadedPage - 1) * pageSize + 1;
  const last = first + rowsOnPage - 1;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 2,
        mt: 1,
      }}
    >
      <Typography variant="body2" color="text.secondary" role="status">
        {rowsOnPage > 0 ? `Rows ${first}–${last} of ${totalRows}` : ''}
      </Typography>
      {pageCount > 1 && (
        <Pagination
          aria-label={label}
          size="small"
          page={Math.min(page, pageCount)}
          count={pageCount}
          disabled={disabled}
          onChange={(_e, next) => onChange(next)}
        />
      )}
    </Box>
  );
};

export default SectionPager;
